import type { User } from "firebase/auth";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { type RangeRow, rangesChange } from "./deals";
import { SkeletonSection } from "./Skeleton";

const LIMITS: { key: keyof api.DealLimits; label: string; percent?: boolean }[] = [
  { key: "rate_max", label: "Highest rate rebate ($ per gal)" },
  { key: "percent_max", label: "Highest percent rebate (% of the market value)", percent: true },
  { key: "flat_max", label: "Highest flat rebate ($ each period)" },
  { key: "consideration_max", label: "Highest added consideration ($)" },
];

/** The next free number of a saved range: "1042", "Full", or "–" for a row not saved yet. */
const nextText = (saved: api.NumberRange[], row: RangeRow) => {
  const found = saved.find((range) => range.state === row.state.trim().toUpperCase() && String(range.first) === row.first && String(range.last) === row.last);
  if (!found || found.next === undefined) return "–";
  return found.next === null ? "Full" : String(found.next);
};

const rowsOf = (ranges: api.NumberRange[]): RangeRow[] => [
  ...ranges.map((range) => ({ state: range.state, first: String(range.first), last: String(range.last) })),
  { state: "", first: "", last: "" },
];

const limitText = (value: number, percent?: boolean) => String(percent ? Math.round(value * 10_000) / 100 : value);

/** Design 9.5 and spec F-ACT-2: the account number range of each state and the deal limits. */
export function SalesSettings({ user }: { user: User }) {
  const [found, setFound] = useState<api.SalesSettings | "failed" | undefined>(undefined);
  const [rows, setRows] = useState<RangeRow[]>([]);
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const status = useRef<HTMLParagraphElement>(null);

  const show = (next: api.SalesSettings) => {
    setFound(next);
    setRows(rowsOf(next.ranges));
    setLimits(Object.fromEntries(LIMITS.map(({ key, percent }) => [key, limitText(next.limits[key], percent)])));
  };

  useEffect(() => {
    user
      .getIdToken()
      .then(api.salesSettings)
      .then(show, () => setFound("failed"));
  }, [user]);

  if (found === undefined) return <SkeletonSection title="Sales terms" rows={14} phoneRows={24} />;
  if (found === "failed") {
    return (
      <fieldset>
        <legend>Sales terms</legend>
        <p className="error">Cannot load the sales terms. Try again later.</p>
      </fieldset>
    );
  }
  const say = (ok: boolean, text: string) => {
    setMessage({ ok, text });
    requestAnimationFrame(() => status.current?.focus());
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    const ranges = rangesChange(rows);
    if ("row" in ranges) {
      say(false, `Row ${ranges.row + 1}: ${ranges.text}`);
      return;
    }
    const values: Partial<api.DealLimits> = {};
    for (const { key, label, percent } of LIMITS) {
      const value = Number(limits[key]);
      if (limits[key].trim() === "" || !Number.isFinite(value) || value < 0 || (percent && value > 100)) {
        say(false, `${label}: give a number of 0 or more${percent ? ", at most 100" : ""}.`);
        return;
      }
      values[key] = percent ? value / 100 : value;
    }
    setSaving(true);
    setMessage(null);
    try {
      show(await api.setSalesSettings(await user.getIdToken(), { ranges: ranges.ranges, limits: values as api.DealLimits }));
      say(true, "Saved. New deals use these terms.");
    } catch (e) {
      const detail = e instanceof api.ApiError && e.status < 500 ? e.detail : undefined;
      say(false, detail ?? "The sales terms were not saved. Try again.");
    } finally {
      setSaving(false);
    }
  };
  const setRow = (index: number, change: Partial<RangeRow>) => {
    setRows((before) => {
      const next = before.map((row, i) => (i === index ? { ...row, ...change } : row));
      const last = next[next.length - 1];
      return last.state || last.first || last.last ? [...next, { state: "", first: "", last: "" }] : next;
    });
    setMessage(null);
  };
  return (
    <form onSubmit={save} noValidate data-testid="sales-terms">
      <fieldset disabled={!found.can_edit || saving}>
        <legend>Sales terms</legend>
        <p className="hint">A new account gets the next free number in the range of its state. A number is never used again.</p>
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Account number ranges">
          <table className="list ranges-table" data-testid="ranges-table">
            <thead>
              <tr>
                <th scope="col">State</th>
                <th scope="col">First number</th>
                <th scope="col">Last number</th>
                <th scope="col">Next free</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index} data-testid="range-row">
                  <td data-label="State">
                    <input aria-label={`State, row ${index + 1}`} maxLength={2} value={row.state} onChange={(e) => setRow(index, { state: e.target.value })} />
                  </td>
                  <td data-label="First number">
                    <input aria-label={`First number, row ${index + 1}`} inputMode="numeric" value={row.first} onChange={(e) => setRow(index, { first: e.target.value })} />
                  </td>
                  <td data-label="Last number">
                    <input aria-label={`Last number, row ${index + 1}`} inputMode="numeric" value={row.last} onChange={(e) => setRow(index, { last: e.target.value })} />
                  </td>
                  <td data-label="Next free" data-testid="range-next">
                    {nextText(found.ranges, row)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="hint">To remove a range, clear its row. The last row is empty, for a new state.</p>
        <p className="hint">A deal with terms above these limits waits for a manager to approve it.</p>
        {LIMITS.map(({ key, label, percent }) => (
          <div className="limit" key={key}>
            <label htmlFor={`sales-${key}`}>{label}</label>
            <input
              id={`sales-${key}`}
              data-testid={`sales-${key}`}
              inputMode="decimal"
              value={limits[key] ?? ""}
              onChange={(e) => {
                setLimits((before) => ({ ...before, [key]: e.target.value }));
                setMessage(null);
              }}
            />
            <p className="hint">Default: {limitText(found.defaults[key], percent)}</p>
          </div>
        ))}
        {found.can_edit && (
          <div className="actions">
            <button className="primary" data-testid="save-sales-terms">
              {saving ? "Saving…" : "Save the sales terms"}
            </button>
          </div>
        )}
      </fieldset>
      <p ref={status} tabIndex={-1} role="status" data-testid="sales-terms-status" className={message && !message.ok ? "error" : undefined}>
        {message?.text}
      </p>
    </form>
  );
}
